import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Cookie } from "lucide-react";

/**
 * Lightweight, non-blocking cookie consent banner.
 * Appears once at the bottom of the screen and remembers the choice in
 * `localStorage.flowtalis_cookie_consent`. Legally required for GDPR/CCPA-adjacent
 * jurisdictions where analytics cookies fire on load.
 *
 * Choices:
 *   - "accepted"  → all cookies + GA4 tracking on
 *   - "essential" → only essential (session token + preferences); analytics off
 */
const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Never show on portal/admin surfaces — those are authenticated app screens
    if (typeof window === "undefined") return;
    if (window.location.pathname.startsWith("/portal") || window.location.pathname.startsWith("/admin")) return;
    const decision = localStorage.getItem("flowtalis_cookie_consent");
    if (!decision) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const decide = (choice) => {
    localStorage.setItem("flowtalis_cookie_consent", choice);
    localStorage.setItem("flowtalis_cookie_consent_at", new Date().toISOString());
    // Signal to the analytics hook so it can boot (or stay dormant) immediately
    window.dispatchEvent(new CustomEvent("flowtalis:cookie-consent", { detail: choice }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md z-[90]"
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent"
    >
      <div className="bg-[var(--ft-black)] text-white rounded-2xl shadow-2xl border border-white/10 p-5 relative">
        <button
          onClick={() => decide("essential")}
          aria-label="Dismiss"
          className="absolute top-3 right-3 text-white/50 hover:text-white transition"
          data-testid="cookie-dismiss"
        >
          <X size={16} />
        </button>
        <div className="flex items-start gap-3">
          <Cookie size={20} className="text-[var(--ft-gold)] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[var(--ft-gold)] font-semibold">
              We use cookies
            </div>
            <p className="text-sm text-white/85 mt-2 leading-relaxed">
              Essential cookies keep FLOWTALIS™ running. With your permission we also use
              analytics cookies to understand how visitors engage with the site.{" "}
              <Link to="/cookies" className="underline text-[var(--ft-gold)] hover:text-white">
                Read our Cookie Policy
              </Link>.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => decide("accepted")}
                className="ft-btn-primary text-xs px-4 py-2"
                data-testid="cookie-accept"
              >
                Accept All
              </button>
              <button
                onClick={() => decide("essential")}
                className="text-xs px-4 py-2 rounded-full border border-white/25 text-white/85 hover:text-white hover:border-white/50 transition"
                data-testid="cookie-essential"
              >
                Essential Only
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
